import { Box, Checkbox, Table, Tbody, Td, Th, Thead, Tooltip, Tr, VStack } from '@chakra-ui/react';
import { t } from 'i18next';
import { useEffect, useMemo, useRef } from 'react';
import { TableInstance } from 'react-table';
import { CustomerSubscriptionsTableSubscriptionFragment } from '~components/organization/CustomerSubscriptionsTable/__generated__/CustomerSubscriptionsTable.graphql';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { WaveSubscriptionStatus } from '~graphql/__generated__/types';
import {
  CustomerSubscriptionsTablePageSizeSelector,
  CustomerSubscriptionsTablePagination,
} from './Pagination';

interface Props {
  table: TableInstance<CustomerSubscriptionsTableSubscriptionFragment>;
  hideExpired: boolean;
  onHideExpiredChange: (hideExpired: boolean) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
}

export function CustomerSubscriptionsTable({
  table,
  hideExpired,
  onHideExpiredChange,
  onSelectionChange,
}: Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, pageCount } = table;

  const isMountedRef = useRef(false);
  const selectedRowIds = useMemo(
    () => Object.keys(table.state.selectedRowIds).sort(),
    [table.state.selectedRowIds],
  );
  const selectedRowIdsKey = selectedRowIds.join(',');

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    onSelectionChange?.(selectedRowIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIdsKey]);
  return (
    <VStack spacing="4" alignItems="stretch">
      <Box>
        <Checkbox isChecked={hideExpired} onChange={(e) => onHideExpiredChange(e.target.checked)}>
          {t('subscriptionHideExpired')}
        </Checkbox>
      </Box>
      <Table {...getTableProps()} variant="simple" width="100%">
        <Thead>
          {headerGroups.map((headerGroup) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();

            return (
              <Tr {...headerGroupProps} key={headerGroupProps.key}>
                {headerGroup.headers.map((column) => {
                  const sortProps = column.getSortByToggleProps();
                  const sortColumn = sortProps.onClick;
                  const headerProps = column.getHeaderProps(sortProps);

                  return (
                    <Th
                      {...headerProps}
                      key={headerProps.key}
                      color={column.isSorted ? 'gray.700' : 'gray.600'}
                      minWidth={`${column.minWidth}px`}
                      maxWidth={`${column.maxWidth}px`}
                      width={column.width}
                      _hover={
                        column.disableSortBy
                          ? {}
                          : {
                              textColor: 'gray.800',
                            }
                      }
                      onClick={sortColumn}
                    >
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Box flex="1">{column.render('Header')}</Box>
                        {column.canSort && (
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <ChevronDownIcon display="inherit" />
                              ) : (
                                <ChevronUpIcon display="inherit" />
                              )
                            ) : (
                              <ChevronDownIcon visibility="hidden" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            const expiredSubscription = row.original.status !== WaveSubscriptionStatus.Active;
            return (
              <Tooltip
                label={expiredSubscription ? t('subscriptionCustomerLinkTooltip2') : ''}
                isDisabled={!expiredSubscription}
                hasArrow
                key={rowProps.key}
              >
                <Tr {...rowProps} {...(expiredSubscription ? { background: 'gray.50' } : {})}>
                  {row.cells.map((cell) => {
                    const cellProps = cell.getCellProps();

                    return (
                      <Td
                        {...cellProps}
                        key={cellProps.key}
                        {...(expiredSubscription ? { opacity: 0.5 } : {})}
                        minWidth={`${cell.column.minWidth}px`}
                        maxWidth={`${cell.column.maxWidth}px`}
                        width={cell.column.width}
                      >
                        {cell.render('Cell')}
                      </Td>
                    );
                  })}
                </Tr>
              </Tooltip>
            );
          })}
        </Tbody>
      </Table>
      {pageCount > 1 && (
        <Box display="flex" justifyContent="flex-end">
          <CustomerSubscriptionsTablePageSizeSelector table={table} />
          <CustomerSubscriptionsTablePagination table={table} />
        </Box>
      )}
    </VStack>
  );
}
